import React, { useState } from "react";
import "../styles/BuyTransferableNumberPage.css";
import { useLocation } from "react-router-dom";

interface Plan {
  id: string;
  name: string;
  price: string;
  digits: string;
  transfers: string;
  color: string;
  description: string;
}

type Step = "selection" | "customize" | "confirm";

const BuyTransferableNumberPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId");
  
  const [step, setStep] = useState<Step>("selection");
  const [selectedTier, setSelectedTier] = useState<Plan | null>(null);
  const [customNumber, setCustomNumber] = useState<string>("");
  const [generatedNumber, setGeneratedNumber] = useState<string>("");
  const [error, setError] = useState<string>("");

  const plans = [

        {
      id: "golden",
      name: "Golden",
      price: "$5",
      digits: "Custom digits",
      transfers: "Unlimited transfers",
      color: "#FFD700",
      description: "Premium experience"
    },
    {
      id: "silver",
      name: "Silver",
      price: "$2",
      digits: "8 random digits",
      transfers: "10 free transfers",
      color: "#C0C0C0",
      description: "Great for work contacts"
    },
    {
      id: "copper",
      name: "Copper",
      price: "$1",
      digits: "12 random digits",
      transfers: "3 free transfers",
      color: "#CD7F32",
      description: "Perfect for basic use"
    }
  ];

  const generateRandomNumber = (length: number): string => {
    let number = "";
    for (let i = 0; i < length; i++) {
      number += Math.floor(Math.random() * 10);
    }
    return number;
  };

  const handlePlanSelect = (tier: Plan): void => {
    setSelectedTier(tier);
    setError("");
    
    if (tier.id === "copper") {
      // Copper goes straight to confirmation
      setStep("confirm");
    } else if (tier.id === "silver") {
      // Silver shows generated number with refresh option
      setGeneratedNumber(generateRandomNumber(8));
      setStep("customize");
    } else if (tier.id === "golden") {
      // Golden allows custom input
      setCustomNumber("");
      setStep("customize");
    }
  };

  const handleRefreshNumber = () => {
    setGeneratedNumber(generateRandomNumber(8));
  };

  const validateCustomNumber = (number: string): string => {
    // Check if it's all digits
    if (!/^\d+$/.test(number)) {
      return "Number must contain only digits";
    }
    // Check length constraints (example: 6-12 digits)
    if (number.length < 1 || number.length > 12) {
      return "Number must be between 1 and 12 digits";
    }
    return "";
  };

  const handleCustomNumberSubmit = (): void => {
    const validationError = validateCustomNumber(customNumber);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStep("confirm");
  };

  const handleFinalizePurchase = (): void => {
    if (!selectedTier) return;
    
    const number = selectedTier.id === "silver" ? generatedNumber : 
                   selectedTier.id === "golden" ? customNumber : 
                   generateRandomNumber(12);
    
    const deepLink = `idless://buynumber?userId=${userId}&tier=${selectedTier.id}&number=${number}&buyReference=${Date.now()}`;
    window.location.href = deepLink;
  };

  const handleCancel = (): void => {
    const deepLink = `idless://buynumber`;
    window.location.href = deepLink;
  };

  const handleBack = (): void => {
    if (step === "customize" || step === "confirm") {
      setStep("selection");
      setSelectedTier(null);
      setError("");
    }
  };

  // Plan Selection View
  if (step === "selection") {
    return (
      <div className="container">
        <div className="contentContainer">
          <div className="logoContainer">
            <img src="/idlesslogo.png" alt="Logo" className="logo" />
          </div>

          <div className="header">
            <h2 className="headerText">Choose Your Plan</h2>
          </div>

          <div className="messageBubble">
            <p className="messageText">
              Select a transferable number tier that fits your needs
            </p>
          </div>

          <div className="plansContainer">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="planCard"
                onClick={() => handlePlanSelect(plan)}
              >
                <div className="planHeader" style={{ backgroundColor: plan.color }}>
                  <h3 className="planName">{plan.name}</h3>
                  <div className="planPrice">{plan.price}</div>
                </div>
                <div className="planBody">
                  <div className="planFeature">
                    <span className="featureIcon">📱</span>
                    <span className="featureText">{plan.digits}</span>
                  </div>
                  <div className="planFeature">
                    <span className="featureIcon">🔄</span>
                    <span className="featureText">{plan.transfers}</span>
                  </div>
                  <p className="planDescription">{plan.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="buttonContainer">
          <button className="button cancelButton" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Customization View (Silver or Golden)
  if (step === "customize" && selectedTier) {
    return (
      <div className="container">
        <div className="contentContainer">
          <div className="logoContainer">
            <img src="/idlesslogo.png" alt="Logo" className="logo" />
          </div>

          <div className="header">
            <h2 className="headerText">
              {selectedTier.id === "silver" ? "Your Number" : "Custom Number"}
            </h2>
          </div>

          {selectedTier.id === "silver" ? (
            <>
              <div className="numberDisplay">
                <div className="displayedNumber">{generatedNumber}</div>
              </div>
              <button className="refreshButton" onClick={handleRefreshNumber}>
                🔄 Generate New Number
              </button>
              <div className="messageBubble">
                <p className="messageText">
                  Keep refreshing until you find a number you like
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="customInputContainer">
                <input
                  type="tel"
                  className="customNumberInput"
                  placeholder="Enter your custom number"
                  value={customNumber}
                  onChange={(e) => setCustomNumber(e.target.value)}
                  maxLength={12}
                />
                {error && <p className="errorText">{error}</p>}
              </div>
              <div className="messageBubble">
                <p className="messageText">
                  Enter 6-12 digits for your custom transferable number
                </p>
              </div>
            </>
          )}
        </div>

        <div className="buttonContainer">
          <button className="button cancelButton" onClick={handleBack}>
            Back
          </button>
          <button
            className="button buyButton"
            onClick={selectedTier.id === "silver" ? () => setStep("confirm") : handleCustomNumberSubmit}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Confirmation View
  if (step === "confirm" && selectedTier) {
    const finalNumber = selectedTier.id === "silver" ? generatedNumber :
                       selectedTier.id === "golden" ? customNumber :
                       "Random 12-digit number";
    
    return (
      <div className="container">
        <div className="contentContainer">
          <div className="logoContainer">
            <img src="/idlesslogo.png" alt="Logo" className="logo" />
          </div>

          <div className="header">
            <h2 className="headerText">Confirm Purchase</h2>
          </div>

          <div className="confirmationCard">
            <div className="confirmRow">
              <span className="confirmLabel">Plan:</span>
              <span className="confirmValue">{selectedTier.name}</span>
            </div>
            <div className="confirmRow">
              <span className="confirmLabel">Price:</span>
              <span className="confirmValue">{selectedTier.price}</span>
            </div>
            <div className="confirmRow">
              <span className="confirmLabel">Number:</span>
              <span className="confirmValue confirmNumber">{finalNumber}</span>
            </div>
            <div className="confirmRow">
              <span className="confirmLabel">Free Transfers:</span>
              <span className="confirmValue">{selectedTier.transfers}</span>
            </div>
          </div>

          <div className="messageBubble">
            <p className="messageText">
              Review your selection and tap Confirm to complete your purchase
            </p>
          </div>
        </div>

        <div className="buttonContainer">
          <button className="button cancelButton" onClick={handleBack}>
            Back
          </button>
          <button className="button buyButton" onClick={handleFinalizePurchase}>
            Confirm Purchase
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default BuyTransferableNumberPage;